import { Injectable } from '@angular/core';
import { AccountType, Rights } from '@shared/enums';
import { Subproject, UserSession } from '@shared/models';

@Injectable()
export class UserRightsService {
  private readonly availableStateByPriority = [
    'dprs',
    'project-setup',
    'project-admin',
    'user-admin/system-admins',
    'user-admin/users',
    'clients',
  ];
  private readonly availableSubprojectStateByPriority = [
    'dprs',
    'project-setup',
  ];
  private readonly availableOfflineStateByPriority = ['dprs'];

  getFirstAllowedState(
    preferred: string,
    subprojectId: number,
    isAppOffline: boolean,
    userDetails: UserSession
  ): string {
    if (
      preferred &&
      this.hasRightsForState(preferred, subprojectId, userDetails)
    ) {
      return this.classifyState(preferred);
    }

    if (isAppOffline) {
      return this.availableOfflineStateByPriority.find((x) => {
        return this.hasRightsForState(x, subprojectId, userDetails);
      });
    } else {
      return this.availableStateByPriority.find((x) => {
        return this.hasRightsForState(x, subprojectId, userDetails);
      });
    }
  }

  getFirstAllowedSubprojectState(
    preferred: string,
    subprojectId: number,
    userDetails: UserSession
  ): string {
    if (
      preferred &&
      this.hasRightsForState(preferred, subprojectId, userDetails)
    ) {
      return this.classifyState(preferred);
    }

    return this.availableSubprojectStateByPriority.find((x) =>
      this.hasRightsForState(x, subprojectId, userDetails)
    );
  }

  getFirstAllowedSubproject(userDetails: UserSession): Subproject {
    return (userDetails?.subprojects || []).find(
      (x) => x.dprScreen !== Rights.None || x.projectSetup !== Rights.None
    );
  }

  hasRightsForState(
    state: string,
    subprojectId: number,
    userDetails: UserSession
  ): boolean {
    switch (this.classifyState(state)) {
      case 'dprs':
      case 'project-setup':
        return this.hasRightsForSubprojectState(
          state,
          subprojectId,
          userDetails
        );
      case 'project-admin':
        return userDetails.settingsRight !== Rights.None;
      case 'clients':
      case 'user-admin/system-admins':
        return userDetails.accountType === AccountType.SystemAdmin;
      case 'user-admin/users':
        return userDetails.userAdminRight !== Rights.None;
    }

    return false;
  }

  hasRightsForSubprojectState(
    state: string,
    subprojectId: number,
    userDetails: UserSession
  ): boolean {
    if (subprojectId == null) {
      return false;
    }

    const subprojectDetails = userDetails.subprojects.find(
      (x) => x.id === subprojectId
    );

    if (!subprojectDetails) {
      return false;
    }

    switch (this.classifyState(state)) {
      case 'dprs':
        return subprojectDetails.dprScreen !== Rights.None;
      case 'project-setup':
        return subprojectDetails.projectSetup !== Rights.None;
    }

    return false;
  }

  private classifyState(state: string): string {
    if (!state) {
      return null;
    }

    if (state.includes('dprs') || state.includes('dpr-details')) {
      return 'dprs';
    }

    if (state.includes('project-setup')) {
      return 'project-setup';
    }

    if (state.includes('project-admin')) {
      return 'project-admin';
    }

    if (state.includes('user-admin/users')) {
      return 'user-admin/users';
    }

    if (state.includes('clients')) {
      return 'clients';
    }

    if (state.includes('user-admin/system-admins')) {
      return 'user-admin/system-admins';
    }

    return null;
  }
}
